import { useMutation, useQuery } from '@apollo/client';
import { CONNECT_PERSONAL_MAILBOX_AUTH, PERSONAL_SMART_INBOX_INTEGRATION, UPDATE_PROFILE } from '../queries.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import { defaultRenderOption } from '../../core/components/Fields/AutoCompleteField.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import { ConnectionStatus, getPersonalSmartInboxConnectionStatus } from '../../document/constants.ts';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS } from '../../../constants.ts';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import Layout from '../../core/components/Layout.tsx';
import Page from '../../core/components/Page';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { PersonalSmartInboxIntegration } from '../components/PersonalSmartInbox/PersonalSmartInboxIntegration.tsx';
import { PersonalSmartInboxIntegrationType } from '../components/PersonalSmartInbox/types.ts';
import { usePersonalInboxConnection } from '../hooks/usePersonalInboxConnection.tsx';
import TeamsIntegrationCard from '../../products/voice_agents/components/TeamsIntegrationCard.tsx';
import { canSeeVoiceAgents, canUsePersonalSmartInbox } from '../utils.ts';

interface ProfileFormValues {
    firstName: string;
    lastName: string;
    email: string;
    language: string;
}
interface ChannelFormValues {
    orderChannel: string;
    rfqChannel: string;
}

interface ExcludedFormValues {
    excludedContent: {
        keywords: string[];
        emailsDomains: string[];
    };
}
interface AuthFormValues {
    status: string;
}

const PROFILE_FORM_DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    email: '',
    language: DEFAULT_LANGUAGE,
};

const CHANNEL_FORM_DEFAULT_VALUES = {
    orderChannel: '',
    rfqChannel: '',
};

const EXCLUDED_CONTENT_FORM_DEFAULT_VALUES = {
    excludedContent: {
        keywords: [],
        emailsDomains: [],
    },
};

const AUTH_FORM_DEFAULT_VALUES = {
    status: ConnectionStatus.Disconnected,
};

const PersonalSmartInboxSection = () => {
    const { t } = useTranslation('users');
    const { publishToast } = useToaster();
    const { user, refetchUser } = useApplicationContext();

    const channelForm = useForm<ChannelFormValues>({ defaultValues: CHANNEL_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const excludedContentForm = useForm<ExcludedFormValues>({
        defaultValues: EXCLUDED_CONTENT_FORM_DEFAULT_VALUES,
        mode: 'onTouched',
    });

    const authForm = useForm<AuthFormValues>({
        defaultValues: AUTH_FORM_DEFAULT_VALUES,
        mode: 'onTouched',
    });

    const [connectOutlook] = useMutation(CONNECT_PERSONAL_MAILBOX_AUTH);

    const personalInboxConnection = usePersonalInboxConnection({ t, authForm, refetchUser });
    const [personalSmartInboxId, setPersonalSmartInboxId] = useState(null);
    const [connectionFailedAt, setConnectionFailedAt] = useState<string | null>(null);
    const [connectedAgentName, setConnectedAgentName] = useState<string | null>(null);

    const { refetch: refetchPersonalSmartInbox } = useQuery(PERSONAL_SMART_INBOX_INTEGRATION, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const setPersonalInboxConnectionStatus = (integration?: PersonalSmartInboxIntegrationType | null) => {
        authForm.reset({ status: getPersonalSmartInboxConnectionStatus(integration) });
        setConnectionFailedAt(integration?.connectionFailedAt ?? null);
        personalInboxConnection.setIsConnected(!!integration);
    };

    const refetchPersonalMailboxStatus = async () => {
        try {
            const { data } = await refetchPersonalSmartInbox();
            setPersonalInboxConnectionStatus(data?.personalSmartInboxIntegration);
            setPersonalSmartInboxId(data?.personalSmartInboxIntegration?.id);
            personalInboxConnection.setConnectedChannels(
                [
                    data?.personalSmartInboxIntegration?.rfqChannel && {
                        rfqChannel: data.personalSmartInboxIntegration.rfqChannel.name,
                    },
                    data?.personalSmartInboxIntegration?.orderChannel && {
                        orderChannel: data.personalSmartInboxIntegration.orderChannel.name,
                    },
                ].filter(Boolean)
            );
            excludedContentForm.reset({
                excludedContent: {
                    keywords: data?.personalSmartInboxIntegration?.manualProcessingKeywords ?? [],
                    emailsDomains: data?.personalSmartInboxIntegration?.manualProcessingSenderEmails ?? [],
                },
            });
            setConnectedAgentName(data?.personalSmartInboxIntegration?.agent?.name || null);
        } catch (error) {
            publishToast({ description: t('personalSmartInbox.errorFetchStatus'), status: 'error' });
            setPersonalInboxConnectionStatus(null);
        }
    };

    useEffect(() => {
        void refetchPersonalMailboxStatus();
    }, [user]);

    return (
        <PersonalSmartInboxIntegration
            translate={t}
            isAdminConsentGranted={user.customer?.personalInboxAdminConsent?.isGranted}
            isAdminConsentUpToDate={user.customer?.personalInboxAdminConsent?.isUpToDate}
            authForm={authForm}
            excludedContentForm={excludedContentForm}
            channelForm={channelForm}
            connectOutlook={connectOutlook}
            refetchPersonalMailboxStatus={refetchPersonalMailboxStatus}
            personalSmartInboxId={personalSmartInboxId}
            connectionFailedAt={connectionFailedAt}
            connectedAgentName={connectedAgentName}
            {...personalInboxConnection}
        />
    );
};

const ProfilePage = () => {
    const { t, i18n } = useTranslation('users');
    const { publishToast } = useToaster();

    const form = useForm<ProfileFormValues>({ defaultValues: PROFILE_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { user, refetchUser } = useApplicationContext();
    const [updateProfile] = useMutation(UPDATE_PROFILE);

    useEffect(() => {
        if (user) {
            form.reset({
                ...PROFILE_FORM_DEFAULT_VALUES,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                language: user.language || DEFAULT_LANGUAGE,
            });
        }
    }, [user]);

    const handleUpdateProfile = async (formData) => {
        try {
            await updateProfile({
                variables: {
                    id: user.id,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    language: formData.language,
                },
            });
            await refetchUser();

            // update the application language
            void i18n.changeLanguage(formData.language.toLowerCase());

            publishToast({ description: t('profile.form.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('profile.form.updateError'), status: 'error' });
        }
    };

    const displayLanguageOptions = LANGUAGE_OPTIONS.map((option) => ({
        ...option,
        disabled: !(i18n.options.supportedLngs || []).includes(option.value),
    }));

    const renderLanguageOption = useCallback((option) => {
        const rendered = defaultRenderOption(option);
        if (!option.disabled) return rendered;
        return (
            <Tooltip content={t('profile.form.languageNotAvailable')} long>
                <div className="w-full">{rendered}</div>
            </Tooltip>
        );
    }, []);

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('profile.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateProfile)}>
                        <Controller
                            name="firstName"
                            control={form.control}
                            rules={{ required: t('profile.form.firstNameRequired') }}
                            render={({ field }) => (
                                <FormField label={t('profile.form.firstName')} required error={formErrors.firstName}>
                                    <StringField required {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="lastName"
                            control={form.control}
                            rules={{ required: t('profile.form.lastNameRequired') }}
                            render={({ field }) => (
                                <FormField label={t('profile.form.lastName')} required error={formErrors.lastName}>
                                    <StringField required {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="email"
                            control={form.control}
                            rules={{ required: t('profile.form.emailRequired') }}
                            render={({ field }) => (
                                <FormField label={t('profile.form.email')} required error={formErrors.email}>
                                    <StringField required {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="language"
                            control={form.control}
                            rules={{ required: t('profile.form.languageRequired') }}
                            render={({ field }) => (
                                <FormField label={t('profile.form.language')} required error={formErrors.language}>
                                    <SelectField
                                        {...field}
                                        options={displayLanguageOptions}
                                        required
                                        renderOption={renderLanguageOption}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={form.formState.isSubmitting}
                            errorCount={countFieldErrors(formErrors)}
                        />
                    </SettingsFormCard>

                    {canUsePersonalSmartInbox(user) && <PersonalSmartInboxSection />}

                    {canSeeVoiceAgents(user) && <TeamsIntegrationCard />}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default ProfilePage;
